import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch} from 'react-redux';
import {useApiQuery} from 'src/hooks/useApiQuery';
import {classify} from 'src/utils/classify';
import {useDebounce} from 'src/hooks/useDebounce';

import {EditorState, convertToRaw} from 'draft-js';

import {
  Toast,
  TOAST_SEMANTIC,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';

import {
  PanelBody,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Checkbox} from '@spaced-out/ui-design-system/lib/components/Checkbox';

import EvaluationScorecardEdit from './evaluation-scorecard-edit';
import EvaluationSubjectiveQuestionsEdit from './evaluation-subjective-questions-edit';
import EvaluationRecommendationEdit from './evaluation-recommendation-edit';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import css from './evaluation-form-edit.css';
import {convertDraftToHTML} from 'src/utils/crm-new/common';
import {
  exportEvaluationFormDraft,
  deleteEvaluationFormDraft,
} from 'src/action-creators/crm';


type EvaluationFormFeedbackScore = {
  parameterId: string;
  score: string | null;
  remark: string | null;
};

type EvaluationFormValue = {
  feedbackScores: EvaluationFormFeedbackScore[];
  answers: Record<string, any>;
  reviewStatus: string | null;
  comment: any;
  noShow?: boolean;
};

export const getTextFromHtml = (html: string) => {
  const span = document.createElement('span');
  span.innerHTML = html;
  return span.textContent || span.innerText;
};

// Helper to serialize EditorState objects for JSON storage
const serializeDraftValue = (value: any): any => {
  if (
    value &&
    typeof value === 'object' &&
    typeof value.getCurrentContent === 'function'
  ) {
    return JSON.stringify(convertToRaw(value.getCurrentContent()));
  }
  return value;
};

// @ts-expect-error - TS7030 - Not all code paths return a value.
const getInitialValue = (review: any, scorecardStepSections: any) => {
  if (review) {
    const {
      feedbackScores,
      answers = [],
      reviewStatus,
      comment,
      noShow,
    } = review;

    // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments.
    const feedbackScoresList = scorecardStepSections.reduce<Array<any>>(
      // @ts-expect-error - TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7006 - Parameter 'section' implicitly has an 'any' type.
      (acc, section) => {
        // @ts-expect-error - TS7006 - Parameter 'param' implicitly has an 'any' type.
        const parameters = section.parameters.map((param) => {
          const filledParameter = (feedbackScores || []).find(
            // @ts-expect-error - TS7006 - Parameter 'fs' implicitly has an 'any' type.
            (fs) => fs.parameterId === param.id,
          );
          if (filledParameter) {
            return {
              parameterId: param.id,
              score: filledParameter.score,
              remark: filledParameter.remark,
            };
          }
          return {
            parameterId: param.id,
            score: null,
            remark: null,
          };
        });

        return [...acc, ...parameters];
      },
      [],
    );

    return {
      feedbackScores: feedbackScoresList,
      // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments. | TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7006 - Parameter 'item' implicitly has an 'any' type.
      answers: answers.reduce<Record<string, any>>((acc, item) => {
        acc[item.questionId] = item.answer;
        return acc;
      }, {}),
      reviewStatus,
      comment,
      noShow: noShow ?? false,
    };
  }
  if (!review) {
    // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments.
    const feedbackScores = scorecardStepSections.reduce<Array<any>>(
      // @ts-expect-error - TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7006 - Parameter 'section' implicitly has an 'any' type.
      (acc, section) => {
        // @ts-expect-error - TS7006 - Parameter 'param' implicitly has an 'any' type.
        const parameters = section.parameters.map((param) => {
          return {
            parameterId: param.id,
            score: null,
            remark: null,
          };
        });

        return [...acc, ...parameters];
      },
      [],
    );

    return {
      feedbackScores,
      answers: {},
      reviewStatus: null,
      comment: '',
      noShow: false,
    };
  }
};

const EvaluationFormEdit = ({
  // @ts-expect-error - TS7031 - Binding element 'scorecardStepSections' implicitly has an 'any' type.
  scorecardStepSections,
  // @ts-expect-error - TS7031 - Binding element 'subjectiveSections' implicitly has an 'any' type.
  subjectiveSections,
  // @ts-expect-error - TS7031 - Binding element 'feedbackConfiguration' implicitly has an 'any' type.
  feedbackConfiguration,
  // @ts-expect-error - TS7031 - Binding element 'review' implicitly has an 'any' type.
  review,
  // @ts-expect-error - TS7031 - Binding element 'step' implicitly has an 'any' type.
  step,
  // @ts-expect-error - TS7031 - Binding element 'stepCandidateId' implicitly has an 'any' type.
  stepCandidateId,
  // @ts-expect-error - TS7031 - Binding element 'token' implicitly has an 'any' type.
  token,
  // @ts-expect-error - TS7031 - Binding element 'tokenId' implicitly has an 'any' type.
  tokenId,
  // @ts-expect-error - TS7031 - Binding element 'onBehalfOfUserId' implicitly has an 'any' type.
  onBehalfOfUserId,
  // @ts-expect-error - TS7031 - Binding element 'alreadyInPanelBody' implicitly has an 'any' type.
  alreadyInPanelBody,
  //on submit props
  // @ts-expect-error - TS7031 - Binding element 'onSubmit' implicitly has an 'any' type.
  onSubmit,
  // @ts-expect-error - TS7031 - Binding element 'onCancel' implicitly has an 'any' type.
  onCancel,
  // @ts-expect-error - TS7031 - Binding element 'isSubmitting' implicitly has an 'any' type.
  isSubmitting,
}) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const hasUserEdited = React.useRef(false);

  // Get the unique key for this feedback (using stepCandidateId + onBehalfOfUserId for uniqueness)
  const draftKey = onBehalfOfUserId
    ? `${stepCandidateId}~${onBehalfOfUserId}`
    : String(stepCandidateId);

  const {data: savedDraft, isLoading: isLoadingSavedDraft} = useApiQuery(
    `/api/v1/draft/${draftKey}`,
    {
      queryOptions: {
        queryKey: ['evaluation-form-draft', draftKey],
      },
      apiOptions: {
        method: 'GET',
        options: {
          apiPath: '/trmapi',
        },
      },
    },
  );

  const draftPayload = React.useMemo(() => {
    if (!savedDraft) {
      return undefined;
    }
    if (typeof savedDraft === 'string') {
      return savedDraft;
    }
    if (
      typeof savedDraft === 'object' &&
      'payload' in savedDraft &&
      typeof savedDraft.payload === 'string'
    ) {
      return savedDraft.payload;
    }
    return undefined;
  }, [savedDraft]);

  const parsedDraft = React.useMemo(() => {
    if (
      savedDraft &&
      typeof savedDraft === 'object' &&
      'feedbackScores' in savedDraft
    ) {
      return savedDraft as EvaluationFormValue;
    }
    if (!draftPayload) {
      return undefined;
    }
    try {
      return JSON.parse(draftPayload) as EvaluationFormValue;
    } catch {
      return undefined;
    }
  }, [draftPayload, savedDraft]);

  const questions = subjectiveSections?.reduce(
    (acc: any, subjectiveSection: any) => {
      return [...acc, ...subjectiveSection.section.questions];
    },
    [],
  );

  const [errorQuestionIds, setErrorQuestionIds] = React.useState([]);
  // Initialize local draft from server payload or base defaults
  const initialEvaluation = React.useMemo(
    () =>
      (parsedDraft ??
        getInitialValue(review, scorecardStepSections)) as EvaluationFormValue,
    [parsedDraft, review, scorecardStepSections],
  );

  const [intermediateEvaluation, setIntermediateEvaluation] = React.useState<
    EvaluationFormValue | undefined
  >(initialEvaluation);

  React.useEffect(() => {
    setIntermediateEvaluation(initialEvaluation);
    hasUserEdited.current = false;
  }, [draftKey, initialEvaluation]);

  // Helper to update draft locally
  const updateField = React.useCallback(
    (updater: (current: EvaluationFormValue) => EvaluationFormValue) => {
      hasUserEdited.current = true;
      setIntermediateEvaluation((current) => {
        const base = current ?? initialEvaluation;
        return updater(base);
      });
    },
    [initialEvaluation],
  );

  // Debounce the evaluation value for auto-save
  const debouncedEvaluation = useDebounce(intermediateEvaluation, 500);

  // Save draft only after user has made changes
  React.useEffect(() => {
    if (!debouncedEvaluation || !hasUserEdited.current) {
      return;
    }
    // Serialize any EditorState objects before saving to ensure proper JSON serialization
    const serializedEvaluation = {
      ...debouncedEvaluation,
      comment: serializeDraftValue(debouncedEvaluation.comment),
      answers: Object.fromEntries(
        Object.entries(debouncedEvaluation.answers).map(([key, value]) => [
          key,
          serializeDraftValue(value),
        ]),
      ),
    };
    dispatch(
      exportEvaluationFormDraft(draftKey, JSON.stringify(serializedEvaluation)),
    );
  }, [debouncedEvaluation, dispatch, draftKey]);

  const {hideScorecard, hideOverallRecommendation, stepType} = step;
  const {hasNote, minFeedbackWords} = feedbackConfiguration || {};
  const emptyEvaluation: EvaluationFormValue = {
    feedbackScores: [],
    answers: {},
    reviewStatus: null,
    comment: '',
    noShow: false,
  };

  const formValueToPayloadValue = () => {
    const evaluation = intermediateEvaluation ?? emptyEvaluation;
    const {feedbackScores, answers, reviewStatus, comment, noShow} = evaluation;

    const htmlComment = convertDraftToHTML(comment ?? '');

    const finalEvaluation = {
      feedbackScores: feedbackScores ?? [],
      answers: answers ?? {},
      reviewStatus,
      comment: htmlComment,
      noShow: noShow ?? false,
    };

    // Skip all mandatory validations if "No Show" is checked
    if (!noShow) {
      // overall recommendation validation
      if (!hideOverallRecommendation) {
        if (!reviewStatus) {
          toastApi.show(
            <Toast semantic={TOAST_SEMANTIC.danger}>
              <ToastTitle>
                {`${t(
                  'Overall_recommendation_section_is_incomplete',
                  'Overall recommendation section is incomplete',
                )}`}
              </ToastTitle>
            </Toast>,
          );
          return;
        }
        const commentWords = getTextFromHtml(htmlComment);

        const commentLength = commentWords
          ? commentWords.split(' ').filter((w) => w !== '').length
          : 0;
        if (hasNote && commentLength < minFeedbackWords) {
          toastApi.show(
            <Toast semantic={TOAST_SEMANTIC.danger}>
              <ToastTitle>
                {`${t(
                  'Minimum_word_limit_Overall_Comment_is',
                  'Minimum word limit Overall Comment is',
                )} ${minFeedbackWords}`}
              </ToastTitle>
            </Toast>,
          );
          return;
        }
        // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'string' and 'EditorState' have no overlap.
        if (htmlComment === EditorState.createEmpty()) {
          toastApi.show(
            <Toast semantic={TOAST_SEMANTIC.danger}>
              <ToastTitle>
                {`${t(
                  'Minimum_word_limit_is',
                  'Minimum word limit is',
                )} ${minFeedbackWords}`}
              </ToastTitle>
            </Toast>,
          );
        }
      }

      // scorecard validation
      if (!hideScorecard) {
        // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments.
        const mandatoryParameterIds = scorecardStepSections
          // @ts-expect-error - TS7006 - Parameter 'section' implicitly has an 'any' type.
          .filter((section) => section.status === 'MANDATORY')
          // @ts-expect-error - TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7006 - Parameter 'section' implicitly has an 'any' type.
          .reduce<Array<any>>((acc, section) => {
            const parameterIds = section.parameters.map(
              // @ts-expect-error - TS7006 - Parameter 'parameter' implicitly has an 'any' type.
              (parameter) => parameter.id,
            );
            acc.push(...parameterIds);
            return acc;
          }, []);

        const isIncomplete = !!mandatoryParameterIds.find(
          // @ts-expect-error - TS7006 - Parameter 'mandatoryParameterId' implicitly has an 'any' type.
          (mandatoryParameterId) => {
            const foundParameter = finalEvaluation.feedbackScores.find(
              (score) =>
                score.parameterId === mandatoryParameterId && !score.score,
            );
            return foundParameter;
          },
        );

        if (isIncomplete) {
          toastApi.show(
            <Toast semantic={TOAST_SEMANTIC.danger}>
              <ToastTitle>
                {`${t(
                  'Scorecard_section_parameters_are_mandatory',
                  'Scorecard section parameters are mandatory',
                )}`}
              </ToastTitle>
            </Toast>,
          );
          return;
        }
      }

      // subjective questions validation
      // @ts-expect-error - TS7006 - Parameter 'question' implicitly has an 'any' type.
      const unansweredMandatoryQuestions = questions.filter((question) => {
        if (
          question.required &&
          question.fieldType === 'TEXT_AREA' &&
          finalEvaluation.answers[question.id] === ''
        ) {
          return true;
        }

        if (
          question.required &&
          question.fieldType === 'NUMBER' &&
          finalEvaluation.answers[question.id] >= 0
        ) {
          return false;
        }
        if (question.required && !finalEvaluation.answers[question.id]) {
          return true;
        }
        return false;
      });

      if (unansweredMandatoryQuestions.length > 0) {
        setErrorQuestionIds(
          // @ts-expect-error - TS7006 - Parameter 'question' implicitly has an 'any' type.
          unansweredMandatoryQuestions.map((question) => question.id),
        );
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>{`${t(
              'Please_answer_the_required_questions',
              'Please answer the required questions',
            )}`}</ToastTitle>
          </Toast>,
        );
        return;
      }
    }

    const formattedEvaluation = {
      ...finalEvaluation,

      answers: questions
        // @ts-expect-error - TS7006 - Parameter 'q' implicitly has an 'any' type.
        .filter((q) => finalEvaluation.answers[q.id] !== undefined)
        // @ts-expect-error - TS7006 - Parameter 'q' implicitly has an 'any' type.
        .map((q) => {
          return {
            questionId: q.id,
            answer:
              q.fieldType === 'TEXT_AREA'
                ? convertDraftToHTML(finalEvaluation.answers[q.id])
                : finalEvaluation.answers[q.id],
          };
        }),
    } as const;

    onSubmit(formattedEvaluation);
  };

  const {feedbackScores, answers, reviewStatus, comment, noShow} =
    intermediateEvaluation ?? emptyEvaluation;

  return (
    <>
      <PanelBody
        className={classify(css.panelBody, {
          [css.panelBodyWithoutPadding]: alreadyInPanelBody,
        })}
      >
        {isLoadingSavedDraft ? (
          <CircularLoader />
        ) : (
          <>
            <div className={css.noShowCheckbox}>
              <Checkbox
                name="no-show"
                value="no-show"
                checked={noShow ?? false}
                onChange={({checked}) => {
                  if (checked) {
                    // Clear all fields and disable them
                    updateField((state) => ({
                      ...state,
                      noShow: true,
                      feedbackScores: (state.feedbackScores || []).map(
                        (fs) => ({
                          ...fs,
                          score: null,
                          remark: null,
                        }),
                      ),
                      answers: {},
                      reviewStatus: null,
                      comment: '',
                    }));
                    setErrorQuestionIds([]);
                  } else {
                    updateField((state) => ({...state, noShow: false}));
                  }
                }}
              >
                {t('Candidate_was_a_Noshow', 'Candidate was a No-show')}
              </Checkbox>
            </div>
            {!hideScorecard && (
              <EvaluationScorecardEdit
                scorecardStepSections={scorecardStepSections}
                feedbackScores={feedbackScores}
                feedbackConfiguration={feedbackConfiguration}
                // @ts-expect-error - TS2322 - Type '{ scorecardStepSections: any; feedbackScores: any; feedbackConfiguration: any; stepId: any; handleScorecardOnChange: ({ parameterId, score }: { parameterId: any; score: any; }) => void; handleRemarkChange: (parameterId: any, remark: any) => void; }' is not assignable to type 'IntrinsicAttributes & { scorecardStepSections: any; feedbackScores: any; feedbackConfiguration: any; handleScorecardOnChange: any; handleRemarkChange: any; }'.
                stepId={step.id}
                // @ts-expect-error - TS7031 - Binding element 'parameterId' implicitly has an 'any' type. | TS7031 - Binding element 'score' implicitly has an 'any' type.
                handleScorecardOnChange={({parameterId, score}) =>
                  updateField((state) => {
                    const feedbackScores = state.feedbackScores || [];
                    const exists = feedbackScores.some(
                      (item) => item.parameterId === parameterId,
                    );
                    const updatedScores = exists
                      ? feedbackScores.map((fs) =>
                          fs.parameterId === parameterId ? {...fs, score} : fs,
                        )
                      : [...feedbackScores, {parameterId, score, remark: null}];
                    return {...state, feedbackScores: updatedScores};
                  })
                }
                // @ts-expect-error - TS7006 - Parameter 'parameterId' implicitly has an 'any' type. | TS7006 - Parameter 'remark' implicitly has an 'any' type.
                handleRemarkChange={(parameterId, remark) => {
                  updateField((state) => ({
                    ...state,
                    feedbackScores: (state.feedbackScores || []).map((fs) =>
                      fs.parameterId === parameterId ? {...fs, remark} : fs,
                    ),
                  }));
                }}
                noShow={noShow ?? false}
              />
            )}
            <EvaluationSubjectiveQuestionsEdit
              answers={answers}
              subjectiveSections={subjectiveSections}
              // @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type. | TS7006 - Parameter 'value' implicitly has an 'any' type.
              onChange={(id, value) =>
                updateField((state) => ({
                  ...state,
                  answers: {...state.answers, [id]: value},
                }))
              }
              token={token}
              tokenId={tokenId}
              errorQuestionIds={errorQuestionIds}
              setErrorQuestionIds={setErrorQuestionIds}
              noShow={noShow ?? false}
            />
            {!hideOverallRecommendation && (
              <EvaluationRecommendationEdit
                reviewStatus={reviewStatus ?? ''}
                stepType={stepType}
                comment={comment ?? ''}
                feedbackConfiguration={feedbackConfiguration}
                onReviewStatusChange={(reviewStatus) =>
                  updateField((state) => ({...state, reviewStatus}))
                }
                onCommentChange={(value) =>
                  updateField((state) => ({...state, comment: value}))
                }
                noShow={noShow ?? false}
              />
            )}
          </>
        )}
      </PanelBody>
      <PanelFooter
        classNames={{
          wrapper: classify(css.panelFooter, {
            [css.panelFooterWithoutPadding]: alreadyInPanelBody,
          }),
        }}
      >
        <Button onClick={onCancel} type="tertiary">
          {t('Cancel', 'Cancel')}
        </Button>
        <Button onClick={formValueToPayloadValue} isLoading={isSubmitting}>
          {t('Submit', 'Submit')}
        </Button>
      </PanelFooter>
    </>
  );
};

export default EvaluationFormEdit;
